  // src/ProfileForm.js
  // Multi-step profile builder — wired to POST /api/students
  // On success: polls /api/matches/:student_id every 3s (60s timeout)
  // then navigates to /dashboard/:student_id

  import { useState, useEffect, useRef } from "react";
  import { useNavigate, useParams, useSearchParams } from "react-router-dom";
  import { API_URL, authHeaders } from "./config";
  import AppHeader from "./AppHeader";
  import { useAuth } from "./AuthContext";
  import institutionList from "./data/institutions.json";

  // ── Constants ──────────────────────────────────────────────────────────────

  // v2: Show Junior/Senior HS, all college, and graduate only.
  // Hidden options kept in ALL_GRADE_LEVELS for future re-enablement.
  const ALL_GRADE_LEVELS = [ // eslint-disable-line no-unused-vars
    "High School — Freshman (9th)",
    "High School — Sophomore (10th)",
    "High School — Junior (11th)",
    "High School — Senior (12th)",
    "College — Freshman (1st year)",
    "College — Sophomore (2nd year)",
    "College — Junior (3rd year)",
    "College — Senior (4th year)",
    "College — Fifth year or beyond",
    "Graduate student",
    "Doctoral student",
  ];
  const GRADE_LEVELS = [
    "High School — Junior (11th)",
    "High School — Senior (12th)",
    "College — Freshman (1st year)",
    "College — Sophomore (2nd year)",
    "College — Junior (3rd year)",
    "College — Senior (4th year)",
    "College — Fifth year or beyond",
    "Graduate student",
  ];

  const STATES = [
    "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
    "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
    "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
    "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
    "New Hampshire","New Jersey","New Mexico","New York","North Carolina",
    "North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island",
    "South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
    "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
    "District of Columbia","International",
  ];

  const INCOME_BRACKETS = [
    "Under $30,000",
    "$30,000 – $48,000",
    "$48,000 – $75,000",
    "$75,000 – $110,000",
    "$110,000 – $150,000",
    "Over $150,000",
    "Prefer not to say",
  ];

  const DEGREE_TYPES = [
    "Associate's",
    "Bachelor's",
    "Master's",
    "Doctoral",
    "Certificate",
    "Other",
  ];

    const STEPS = ["Identity", "Academic", "Profile", "Financial"];

  // ── #41 v2 College Profile Helpers ──
  const isHighSchool = (gl) => gl?.startsWith("High School");

  const isCollegeUndergrad = (gl) => gl?.startsWith("College");

  const isGraduate = (gl) => gl === "Graduate student" || gl === "Doctoral student";

  const isCollegeOrAbove = (gl) => isCollegeUndergrad(gl) || isGraduate(gl);

  const isGradBound = (gl) => [
    "College — Junior (3rd year)",
    "College — Senior (4th year)",
    "College — Fifth year or beyond",
    "Graduate student",
    "Doctoral student",
  ].includes(gl);

  const GRADUATE_PROGRAMS = [
    "Dental",
    "Medical / Pre-Med",
    "Law",
    "MBA / Business",
    "Engineering",
    "Nursing",
    "Pharmacy",
    "Education",
    "Social Work",
    "Other Health Sciences",
    "Sciences",
    "Arts / Humanities",
    "Other",
    "Not planning graduate school",
  ];

  const POLL_INTERVAL_MS  = 3000;
  const POLL_TIMEOUT_MS   = 60000;

  // ── Helpers ────────────────────────────────────────────────────────────────

  function parseTagInput(val) {
    // Splits comma-separated string into trimmed non-empty array
    return val.split(",").map(s => s.trim()).filter(Boolean);
  }

  // ── Sub-components ─────────────────────────────────────────────────────────

  function StepIndicator({ current }) {
    return (
      <div style={styles.stepRow}>
        {STEPS.map((label, i) => {
          const done    = i < current;
          const active  = i === current;
          return (
            <div key={label} style={styles.stepItem}>
              <div style={{
                ...styles.stepCircle,
                background: done ? "#1a3a5c" : active ? "#c9a84c" : "transparent",
                borderColor: done || active ? (done ? "#1a3a5c" : "#c9a84c") : "#ccc",
                color: done || active ? "#fff" : "#999",
              }}>
                {done ? "✓" : i + 1}
              </div>
              <span style={{
                ...styles.stepLabel,
                color: active ? "#1a3a5c" : done ? "#555" : "#aaa",
                fontWeight: active ? 700 : 400,
              }}>{label}</span>
              {i < STEPS.length - 1 && (
                <div style={{
                  ...styles.stepLine,
                  background: done ? "#1a3a5c" : "#e0e0e0",
                }} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function FieldError({ errors, field }) {
    const err = errors?.[field];
    if (!err) return null;
    return <p style={styles.fieldError}>{err}</p>;
  }

  function FieldWarn({ show, message }) {
    if (!show) return null;
    return <p style={styles.fieldWarn}>⚠ {message}</p>;
  }

  function Label({ children, required }) {
    return (
      <label style={styles.label}>
        {children}
        {required && <span style={styles.required}> *</span>}
      </label>
    );
  }

  function Input({ style, ...props }) {
    return <input style={{ ...styles.input, ...style }} {...props} />;
  }

  function Select({ children, style, ...props }) {
    return (
      <select style={{ ...styles.input, ...style }} {...props}>
        {children}
      </select>
    );
  }

  function TagInput({ value, onChange, placeholder }) {
    // value is an array; display as comma-separated string for editing
    const [raw, setRaw] = useState(value.join(", "));
    function handle(e) {
      setRaw(e.target.value);
      onChange(parseTagInput(e.target.value));
    }
    return (
      <div>
        <Input
          value={raw}
          onChange={handle}
          placeholder={placeholder || "Separate with commas"}
        />
        {value.length > 0 && (
          <div style={styles.tagRow}>
            {value.map(t => (
              <span key={t} style={styles.tag}>{t}</span>
            ))}
          </div>
        )}
      </div>
    );
  }
  // ── Institution autocomplete (powered by Peterson's CD10_INST) ──
  function InstitutionPicker({ value, onChange }) {
    const [query, setQuery] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);

    const filtered = query.length >= 2
      ? institutionList.filter(name =>
          name.toLowerCase().includes(query.toLowerCase())
        ).slice(0, 8)
      : [];

    const addSchool = (name) => {
      if (!value.includes(name)) {
        onChange([...value, name]);
      }
      setQuery("");
      setShowSuggestions(false);
    };

    const removeSchool = (name) => {
      onChange(value.filter(v => v !== name));
    };

    return (
      <div>
        {value.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
            {value.map(name => (
              <span key={name} style={{
                background: "#e8f4e8", padding: "4px 10px", borderRadius: "12px",
                fontSize: "13px", display: "flex", alignItems: "center", gap: "6px"
              }}>
                {name}
                <button
                  type="button"
                  onClick={() => removeSchool(name)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: "14px", color: "#888", padding: 0, lineHeight: 1
                  }}
                >×</button>
              </span>
            ))}
          </div>
        )}
        <div style={{ position: "relative" }}>
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Start typing a school name..."
            style={{
              width: "100%", padding: "8px 12px", border: "1px solid #ccc",
              borderRadius: "6px", fontSize: "14px", boxSizing: "border-box"
            }}
          />
          {showSuggestions && filtered.length > 0 && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
              background: "#fff", border: "1px solid #ddd", borderRadius: "6px",
              maxHeight: "200px", overflowY: "auto", marginTop: "4px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
            }}>
              {filtered.map(name => (
                <div
                  key={name}
                  onMouseDown={() => addSchool(name)}
                  style={{
                    padding: "8px 12px", cursor: "pointer", fontSize: "13px",
                    borderBottom: "1px solid #f0f0f0"
                  }}
                  onMouseEnter={e => e.target.style.background = "#f5f5f5"}
                  onMouseLeave={e => e.target.style.background = "#fff"}
                >
                  {name}
                </div>
              ))}
            </div>
          )}
          {showSuggestions && query.length >= 2 && filtered.length === 0 && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0,
              background: "#fff", border: "1px solid #ddd", borderRadius: "6px",
              padding: "8px 12px", marginTop: "4px", fontSize: "13px", color: "#999"
            }}>
              No matching schools found
            </div>
          )}
        </div>
      </div>
    );
  }
  function SectionHeading({ children }) {
    return <h2 style={styles.sectionHeading}>{children}</h2>;
  }

  function FormGroup({ children }) {
    return <div style={styles.formGroup}>{children}</div>;
  }

  function Row({ children }) {
    return <div style={styles.row}>{children}</div>;
  }

  // ── Step components ────────────────────────────────────────────────────────

  function StepIdentity({ data, onChange, errors }) {
    return (
      <div>
        <SectionHeading>About You</SectionHeading>
        <p style={styles.sectionSubtitle}>
          These fields are required. They identify you in the system and help us find scholarships you're eligible for.
        </p>

        <Row>
          <FormGroup>
            <Label required>First Name</Label>
            <Input
              value={data.first_name}
              onChange={e => onChange("first_name", e.target.value)}
              placeholder="Jane"
              autoFocus
            />
            <FieldError errors={errors} field="first_name" />
          </FormGroup>
          <FormGroup>
            <Label required>Last Name</Label>
            <Input
              value={data.last_name}
              onChange={e => onChange("last_name", e.target.value)}
              placeholder="Smith"
            />
            <FieldError errors={errors} field="last_name" />
          </FormGroup>
        </Row>

        <FormGroup>
          <Label required>Email Address</Label>
          <Input
            type="email"
            value={data.email}
            onChange={e => onChange("email", e.target.value)}
            placeholder="jane@example.com"
          />
          <FieldError errors={errors} field="email" />
        </FormGroup>

        <Row>
          <FormGroup>
            <Label required>Current Grade Level</Label>
            <Select
              value={data.grade_level}
              onChange={e => onChange("grade_level", e.target.value)}
            >
              <option value="">— Select —</option>
              {GRADE_LEVELS.map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </Select>
            <FieldError errors={errors} field="grade_level" />
          </FormGroup>
          <FormGroup>
            <Label required>State of Residence</Label>
            <Select
              value={data.state_of_residence}
              onChange={e => onChange("state_of_residence", e.target.value)}
            >
              <option value="">— Select —</option>
              {STATES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
            <FieldError errors={errors} field="state_of_residence" />
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label>Date of Birth (optional)</Label>
            <Input
              type="date"
              value={data.date_of_birth}
              onChange={e => onChange("date_of_birth", e.target.value)}
            />
          </FormGroup>
                   <FormGroup>
            <Label>Citizenship</Label>
            <Select
              value={data.citizenship}
              onChange={e => onChange("citizenship", e.target.value)}
            >
              <option value="">— Select —</option>
              <option value="U.S. Citizen">U.S. Citizen</option>
              <option value="U.S. Permanent Resident">U.S. Permanent Resident</option>
              <option value="DACA / Dreamer">DACA / Dreamer</option>
              <option value="International Student (Visa)">International Student (Visa)</option>
              <option value="Refugee / Asylee">Refugee / Asylee</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </Select>
          </FormGroup>
        </Row>

        <Row>
                   <FormGroup>
            <Label>Gender</Label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                id="genderCheckbox"
                checked={data.gender === "Male" || data.gender === "Female"}
                onChange={e => onChange("gender", e.target.checked ? "Female" : "")}
                style={{ width: 16, height: 16, cursor: "pointer" }}
              />
                <label htmlFor="genderCheckbox" style={{ fontSize: "0.85rem", fontFamily: "sans-serif", color: "#2c3e50", cursor: "pointer" }}>
                Include gender-based scholarships
              </label>
            </div>
            {(data.gender === "Male" || data.gender === "Female") && (
              <select
                value={data.gender}
                onChange={e => onChange("gender", e.target.value)}
                style={{ marginTop: 6, padding: "8px 12px", borderRadius: 6, border: "1px solid #D8DEE4", fontSize: 14, color: "#4A5568" }}
              >
                <option value="Female">Female</option>
                <option value="Male">Male</option>
              </select>
            )}
          </FormGroup>
                                      <FormGroup>
            <Label>Ethnicity</Label>
            {(() => {
              const ETHNICITY_OPTIONS = [
                "American Indian / Alaska Native",
                "Asian",
                "Black / African American",
                "Hispanic / Latino",
                "Native Hawaiian / Pacific Islander",
                "White / Caucasian",
                "Middle Eastern / North African",
                "Two or More Races",
                "Prefer not to say",
              ];
              const selected = Array.isArray(data.ethnicity) ? data.ethnicity : (data.ethnicity ? [data.ethnicity] : []);
              const toggle = (val) => {
                const updated = selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val];
                onChange("ethnicity", updated);
              };
              return (
                <div>
                  <select
                    value=""
                    onChange={e => { if (e.target.value) toggle(e.target.value); }}
                    style={{ width: "100%", boxSizing: "border-box", padding: "10px 14px", border: "1.5px solid #d0d8e4", borderRadius: 6, fontSize: "0.95rem", color: "#2c3e50", fontFamily: "sans-serif", background: "#fafcff" }}
                  >
                    <option value="">Select ethnicity (optional, multi-select)</option>
                    {ETHNICITY_OPTIONS.filter(o => !selected.includes(o)).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {selected.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                      {selected.map(s => (
                        <span key={s} style={{ fontSize: "0.8rem", background: "#EBF5EE", color: "#2B7A4B", padding: "3px 8px", borderRadius: 4, fontFamily: "sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {s}
                          <span onClick={() => toggle(s)} style={{ cursor: "pointer", fontWeight: 700, fontSize: "0.9rem" }}>×</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </FormGroup>
        </Row>

        <FormGroup>
          <label style={styles.checkRow}>
            <input
              type="checkbox"
              checked={data.is_first_generation}
              onChange={e => onChange("is_first_generation", e.target.checked)}
              style={styles.checkbox}
            />
            <span>I am a first-generation college student</span>
          </label>
        </FormGroup>
      </div>
    );
  }

  function StepAcademic({ data, onChange, errors }) {
    return (
      <div>
        <SectionHeading>Academic Background</SectionHeading>
        <p style={styles.sectionSubtitle}>
          Test scores and GPA help match you to merit-based scholarships. All fields optional unless marked.
        </p>

        <Row>
          <FormGroup>
            <Label required>GPA (at least one required)</Label>
            <Input
              type="number"
              min="0" max="5" step="0.01"
              value={data.gpa_unweighted}
              onChange={e => onChange("gpa_unweighted", e.target.value)}
              placeholder="0.0 – 5.0"
            />
            <FieldError errors={errors} field="gpa_unweighted" />
          </FormGroup>
          <FormGroup>
            <Label>GPA (Weighted)</Label>
            <Input
              type="number"
              min="0" max="6" step="0.01"
              value={data.gpa_weighted}
              onChange={e => onChange("gpa_weighted", e.target.value)}
              placeholder="0.0 – 6.0"
            />
            <FieldError errors={errors} field="gpa_weighted" />
          </FormGroup>
        </Row>

        <Row>
        <FormGroup>
          <Label>SAT Score</Label>
          <Input
            type="number"
            min="400" max="1600"
            value={data.sat_score}
            onChange={e => onChange("sat_score", e.target.value)}
            placeholder="400 – 1600"
          />
          <p style={styles.hint}>Enter your superscore if you've taken the SAT multiple times</p>
          <FieldError errors={errors} field="sat_score" />
        </FormGroup>
        <FormGroup>
          <Label>ACT Composite Score</Label>
          <Input
            type="number"
            min="1" max="36"
            value={data.act_score}
            onChange={e => onChange("act_score", e.target.value)}
            placeholder="1 – 36"
          />
          <p style={styles.hint}>Enter your superscore if you've taken the ACT multiple times</p>
          <FieldError errors={errors} field="act_score" />
        </FormGroup>
      </Row>

      <FormGroup>
        <Label>ACT Science Score</Label>
        <Input
          type="number"
          min="1" max="36"
          value={data.act_science_score}
          onChange={e => onChange("act_science_score", e.target.value)}
          placeholder="1 – 36 (optional)"
          style={{ maxWidth: "220px" }}
        />
        <p style={styles.hint}>The ACT Science section is scored separately — enter it here if you took it</p>
        <FieldError errors={errors} field="act_science_score" />
      </FormGroup>

        {/* ── High School students: show HS fields inline ── */}
        {isHighSchool(data.grade_level) && (
        <>
        <Row>
          <FormGroup>
            <Label>High School Name</Label>
            <Input
              value={data.high_school_name}
              onChange={e => onChange("high_school_name", e.target.value)}
              placeholder="e.g. Arapahoe High School"
            />
          </FormGroup>
        </Row>
        <Row>
          <FormGroup>
            <Label>High School City</Label>
            <Input
              value={data.high_school_city}
              onChange={e => onChange("high_school_city", e.target.value)}
              placeholder="e.g. Aurora"
            />
          </FormGroup>
          <FormGroup>
            <Label>High School State</Label>
            <Select
              value={data.high_school_state}
              onChange={e => onChange("high_school_state", e.target.value)}
            >
              <option value="">— Select —</option>
              {STATES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </FormGroup>
        </Row>
        <Row>
          <FormGroup>
            <Label required>High School Graduation Date</Label>
            <Input
              type="month"
              value={data.graduation_date}
              onChange={e => onChange("graduation_date", e.target.value)}
            />
            <FieldError errors={errors} field="graduation_date" />
          </FormGroup>
          <FormGroup>
            <Label>Class Rank</Label>
            <Input
              value={data.class_rank}
              onChange={e => onChange("class_rank", e.target.value)}
              placeholder="e.g. 12 of 350"
            />
          </FormGroup>
        </Row>

        <FormGroup>
          <Label>Expected College Degree</Label>
          <Select
            value={data.degree_type}
            onChange={e => onChange("degree_type", e.target.value)}
            style={{ maxWidth: "280px" }}
          >
            <option value="">— Select —</option>
            {DEGREE_TYPES.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </Select>
          <p style={styles.hint}>What degree are you planning to pursue? This drives scholarship matching.</p>
        </FormGroup>

        <FormGroup>
          <Label>PSAT Score</Label>
          <Input
            type="number"
            min="320" max="1520"
            value={data.psat_score}
            onChange={e => onChange("psat_score", e.target.value)}
            placeholder="320 – 1520"
            style={{ maxWidth: "220px" }}
          />
        </FormGroup>

        <FormGroup>
          <Label>AP Courses</Label>
          <TagInput
            value={data.ap_courses}
            onChange={val => onChange("ap_courses", val)}
            placeholder="e.g. AP Biology, AP Calculus BC, AP US History"
          />
          <p style={styles.hint}>Separate with commas</p>
        </FormGroup>

        <FormGroup>
          <Label>IB Courses</Label>
          <TagInput
            value={data.ib_courses}
            onChange={val => onChange("ib_courses", val)}
            placeholder="e.g. IB Math HL, IB English SL, IB Biology HL"
          />
          <p style={styles.hint}>International Baccalaureate courses — separate with commas</p>
        </FormGroup>
        </>
        )}

        {/* ── College / Graduate students: HS Background section + college fields ── */}
        {isCollegeOrAbove(data.grade_level) && (
        <>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: "#2D3748", margin: "24px 0 12px", borderBottom: "1px solid #E2E8F0", paddingBottom: 8 }}>High School Background</h3>
        <Row>
          <FormGroup>
            <Label>High School Name</Label>
            <Input
              value={data.high_school_name}
              onChange={e => onChange("high_school_name", e.target.value)}
              placeholder="e.g. Arapahoe High School"
            />
          </FormGroup>
        </Row>
        <Row>
          <FormGroup>
            <Label>High School City</Label>
            <Input
              value={data.high_school_city}
              onChange={e => onChange("high_school_city", e.target.value)}
              placeholder="e.g. Aurora"
            />
          </FormGroup>
          <FormGroup>
            <Label>High School State</Label>
            <Select
              value={data.high_school_state}
              onChange={e => onChange("high_school_state", e.target.value)}
            >
              <option value="">— Select —</option>
              {STATES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </FormGroup>
        </Row>
        <FormGroup>
          <Label>High School Graduation Date</Label>
          <Input
            type="month"
            value={data.graduation_date}
            onChange={e => onChange("graduation_date", e.target.value)}
          />
        </FormGroup>

        <h3 style={{ fontSize: 16, fontWeight: 600, color: "#2D3748", margin: "24px 0 12px", borderBottom: "1px solid #E2E8F0", paddingBottom: 8 }}>College / University</h3>
        <Row>
          <FormGroup>
            <Label required>Current College / University</Label>
            <Input
              value={data.current_school}
              onChange={e => onChange("current_school", e.target.value)}
              placeholder="e.g. University of Colorado"
            />
            <FieldError errors={errors} field="current_school" />
          </FormGroup>
        </Row>
        <Row>
          <FormGroup>
            <Label required>Expected Graduation Date</Label>
            <Input
              type="month"
              value={data.expected_graduation_date || data.graduation_date}
              onChange={e => onChange("expected_graduation_date", e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Expected College Degree</Label>
            <Select
              value={data.degree_type}
              onChange={e => onChange("degree_type", e.target.value)}
              style={{ maxWidth: "280px" }}
            >
              <option value="">— Select —</option>
              {DEGREE_TYPES.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </Select>
          </FormGroup>
        </Row>
        </>
        )}

        {/* ── HS only: Expected College Degree hidden (they haven't picked yet) ── */}
        {/* ── College/Grad: shown inline above ── */}
      </div>
    );
  }

  function StepProfile({ data, onChange, warns, errors }) {
    return (
      <div>
        <SectionHeading>Interests & Activities</SectionHeading>
        <p style={styles.sectionSubtitle}>
          The more you share here, the better your matches. The highlighted fields below
          significantly improve match quality.
        </p>

        {/* Strongly recommended */}
        <div style={styles.recommendedBlock}>
          <p style={styles.recommendedLabel}>⭐ Strongly Recommended</p>

          <FormGroup>
          <Label required>Intended Majors</Label>
            <TagInput
              value={data.intended_majors}
              onChange={val => onChange("intended_majors", val)}
              placeholder="e.g. Computer Science, Biology, Business"
            />
            <FieldError errors={errors} field="intended_majors" />
          </FormGroup>

                    <FormGroup>
            <label style={styles.checkRow}>
              <input
                type="checkbox"
                checked={data.financial_aid_needed}
                onChange={e => onChange("financial_aid_needed", e.target.checked)}
                style={styles.checkbox}
              />
              <span>I am seeking financial aid / need-based scholarships</span>
            </label>
            <FieldWarn show={warns.financial_aid_needed} message="Indicating financial need unlocks a larger pool of need-based scholarships." />
          </FormGroup>

          {isGradBound(data.grade_level) && (
          <FormGroup>
            <Label>Intended Graduate Program</Label>
            <Select
              value={data.intended_graduate_program}
              onChange={e => onChange("intended_graduate_program", e.target.value)}
              style={{ maxWidth: "320px" }}
            >
              <option value="">— Select —</option>
              {GRADUATE_PROGRAMS.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </Select>
            <p style={styles.hint}>Helps match you to program-specific scholarships</p>
          </FormGroup>
          )}
        </div>

        <FormGroup>
          <Label>Career Interests</Label>
          <TagInput
            value={data.career_interests}
            onChange={val => onChange("career_interests", val)}
            placeholder="e.g. Medicine, Engineering, Education"
          />
        </FormGroup>

        <FormGroup>
          <Label>Sports</Label>
          <TagInput
            value={data.sports}
            onChange={val => onChange("sports", val)}
            placeholder="e.g. Soccer, Swimming, Track & Field"
          />
        </FormGroup>

        <FormGroup>
          <Label>Clubs & Organizations</Label>
          <TagInput
            value={data.clubs}
            onChange={val => onChange("clubs", val)}
            placeholder="e.g. Student Council, Debate, Key Club"
          />
        </FormGroup>

        <FormGroup>
          <Label>Leadership Roles</Label>
          <TagInput
            value={data.leadership_roles}
            onChange={val => onChange("leadership_roles", val)}
            placeholder="e.g. Club President, Team Captain"
          />
        </FormGroup>

        <FormGroup>
          <Label>Awards & Honors</Label>
          <TagInput
            value={data.awards_honors}
            onChange={val => onChange("awards_honors", val)}
            placeholder="e.g. National Merit Semifinalist, Eagle Scout"
          />
        </FormGroup>

        <FormGroup>
          <Label>Volunteer Hours</Label>
          <Input
            type="number"
            min="0"
            value={data.volunteer_hours}
            onChange={e => onChange("volunteer_hours", e.target.value)}
            placeholder="Total hours"
            style={{ maxWidth: "180px" }}
          />
        </FormGroup>

        <FormGroup>
          <Label>Other Organizations</Label>
          <TagInput
            value={data.other_orgs}
            onChange={val => onChange("other_orgs", val)}
            placeholder="e.g. NAACP, 4-H, Boys & Girls Club"
          />
        </FormGroup>

        <FormGroup>
          <Label>Preferred Regions for College</Label>
          <TagInput
            value={data.preferred_regions}
            onChange={val => onChange("preferred_regions", val)}
            placeholder="e.g. Northeast, Pacific Northwest"
          />
        </FormGroup>

        <FormGroup>
          <Label>{isCollegeOrAbove(data.grade_level) ? "Current or Target Colleges / Universities" : "Target Schools"}</Label>
          <p style={styles.hint}>
            {isCollegeOrAbove(data.grade_level)
              ? "Select your current school and any you plan to transfer to. This filters out scholarships restricted to other institutions."
              : "Select the school(s) you plan to attend. This filters out scholarships restricted to other institutions. Leave empty if you haven't decided yet."}
          </p>
          <InstitutionPicker
            value={data.target_schools}
            onChange={val => onChange("target_schools", val)}
          />
        </FormGroup>
        {/* ── Military / ROTC Interest ── */}
        <FormGroup>
          <label style={styles.checkRow}>
            <input
              type="checkbox"
              checked={data.military_interest || false}
              onChange={e => {
                onChange("military_interest", e.target.checked);
                if (!e.target.checked) onChange("military_details", []);
              }}
              style={styles.checkbox}
            />
            <span>I'm interested in military or ROTC scholarships</span>
          </label>
        </FormGroup>

        {data.military_interest && (
          <FormGroup>
            <Label>Military Connection (select all that apply)</Label>
            {[
              { key: "rotc_interest", label: "Interested in ROTC" },
              { key: "active_duty", label: "Currently serving / plan to enlist" },
              { key: "parent_active", label: "Parent/guardian is active duty" },
              { key: "parent_veteran", label: "Parent/guardian is a veteran" },
              { key: "parent_kd_pow", label: "Parent/guardian was killed, disabled, or MIA/POW in service" },
            ].map(opt => (
              <label key={opt.key} style={{ ...styles.checkRow, marginBottom: "6px" }}>
                <input
                  type="checkbox"
                  checked={(data.military_details || []).includes(opt.key)}
                  onChange={e => {
                    const current = data.military_details || [];
                    const updated = e.target.checked
                      ? [...current, opt.key]
                      : current.filter(k => k !== opt.key);
                    onChange("military_details", updated);
                  }}
                  style={styles.checkbox}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </FormGroup>
        )}
      </div>
    );
  }

  function StepFinancial({ data, onChange }) {
    return (
      <div>
        <SectionHeading>Financial Information</SectionHeading>
        <p style={styles.sectionSubtitle}>
          All financial fields are optional. They unlock need-based scholarships and employer-sponsored awards.
        </p>

        <FormGroup>
          <Label>Household Income Bracket</Label>
          <Select
            value={data.household_income_bracket}
            onChange={e => onChange("household_income_bracket", e.target.value)}
            style={{ maxWidth: "320px" }}
          >
            <option value="">— Prefer not to say —</option>
            {INCOME_BRACKETS.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </Select>
        </FormGroup>

        <Row>
          <FormGroup>
            <Label>Household Size</Label>
            <Input
              type="number"
              min="1"
              value={data.household_size}
              onChange={e => onChange("household_size", e.target.value)}
              placeholder="Number of people"
              style={{ maxWidth: "180px" }}
            />
          </FormGroup>
          <FormGroup>
            <Label>Others in College</Label>
            <Input
              type="number"
              min="0"
              value={data.in_college_count}
              onChange={e => onChange("in_college_count", e.target.value)}
              placeholder="Siblings in college"
              style={{ maxWidth: "180px" }}
            />
          </FormGroup>
        </Row>

        <FormGroup>
          <Label>Dependency Status</Label>
          <Select
            value={data.dependency_status}
            onChange={e => onChange("dependency_status", e.target.value)}
            style={{ maxWidth: "280px" }}
          >
            <option value="">— Select —</option>
            <option value="dependent">Dependent</option>
            <option value="independent">Independent</option>
          </Select>
        </FormGroup>

        <Row>
          <FormGroup>
            <Label>Parent / Guardian Employer 1</Label>
            <Input
              value={data.parent_employer_1}
              onChange={e => onChange("parent_employer_1", e.target.value)}
              placeholder="Company name"
            />
            <p style={styles.hint}>Some employers offer scholarship programs for dependents.</p>
          </FormGroup>
          <FormGroup>
            <Label>Parent / Guardian Employer 2</Label>
            <Input
              value={data.parent_employer_2}
              onChange={e => onChange("parent_employer_2", e.target.value)}
              placeholder="Company name"
            />
          </FormGroup>
        </Row>

        <div style={styles.reviewBlock}>
          <p style={styles.reviewTitle}>Ready to find your scholarships?</p>
          <p style={styles.reviewText}>
            After you submit, Your Scholarship Finder will analyze your profile and return personalized
            scholarship matches — usually within 60 seconds.
          </p>
        </div>
      </div>
    );
  }

  // ── Polling ────────────────────────────────────────────────────────────────

  async function pollForMatches(studentId, onSuccess, onTimeout) {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    async function attempt() {
      if (Date.now() >= deadline) { onTimeout(); return; }
      try {
        const res = await fetch(`${API_URL}/api/matches/${studentId}`, {
          headers: authHeaders()
        });
        const json = await res.json();
        const results = json.results ?? json ?? [];
        if (Array.isArray(results) && results.length > 0) {
          onSuccess();
        } else {
          setTimeout(attempt, POLL_INTERVAL_MS);
        }
      } catch {
        setTimeout(attempt, POLL_INTERVAL_MS);
      }
    }
    attempt();
  }

  // ── Main component ─────────────────────────────────────────────────────────

  const EMPTY = {
    // Identity
    first_name: "", last_name: "", email: "",
    grade_level: "", state_of_residence: "",
    date_of_birth: "", citizenship: "", gender: "", ethnicity: "",
    is_first_generation: false,
    // Academic
    gpa_unweighted: "", gpa_weighted: "",
    sat_score: "", act_score: "",
    current_school: "", high_school_name: "",
    high_school_city: "", high_school_state: "",
    graduation_date: "", class_rank: "", psat_score: "",
    ap_courses: [], ib_courses: [], degree_type: "",
    act_science_score: "",
    // Profile
    intended_majors: [], career_interests: [],
    financial_aid_needed: false,
    sports: [], clubs: [], leadership_roles: [],
    awards_honors: [], volunteer_hours: "",
    other_orgs: [], preferred_regions: [], target_schools: [],
    military_interest: false, military_details: [],
    intended_graduate_program: "",
    // Financial
    household_income_bracket: "", household_size: "",
    in_college_count: "", dependency_status: "",
    parent_employer_1: "", parent_employer_2: "",
  };

  export default function ProfileForm() {
    const navigate = useNavigate();
    const { studentId } = useParams();
    const [searchParams] = useSearchParams();
    const parentStudentId = searchParams.get('studentId');
    const isParentMode = !!parentStudentId;
    const isEditMode = !!studentId;
    const { setCurrentStudentId } = useAuth();
    const [step, setStep]       = useState(0);
    
    const [data, setData]       = useState(EMPTY);
    const [errors, setErrors]   = useState({});
    const [warns, setWarns]     = useState({});
    const [status, setStatus]   = useState("idle"); // idle | submitting | polling | timeout | error
    const [apiError, setApiError] = useState("");
    const studentIdRef = useRef(null); 
  // Parent mode: pre-fill name and email from existing student record
  useEffect(() => {
    if (!isParentMode || !parentStudentId) return;
    fetch(`${API_URL}/api/students/${parentStudentId}`, {
      headers: authHeaders()
    })
      .then(r => r.json())
      .then(student => {
        const nameParts = (student.full_name || "").split(" ");
        setData(d => ({
          ...d,
          first_name: nameParts[0] || "",
          last_name: nameParts.slice(1).join(" ") || "",
          email: student.email || "",
        }));
      })
      .catch(err => console.error("Failed to load student:", err));
  }, [isParentMode, parentStudentId]);
    useEffect(() => {
    if (!isEditMode) return;
    fetch(`${API_URL}/api/students/${studentId}`, {
      headers: authHeaders()
    })
      .then(r => r.json())
      .then(profile => {
        setData({
          first_name:               (profile.full_name || "").split(" ")[0] || "",
          last_name:                (profile.full_name || "").split(" ").slice(1).join(" ") || "",
          email:                    profile.email || "",
          grade_level:              profile.grade_level || "",
          state_of_residence:       profile.state_of_residence || "",
          date_of_birth:            profile.date_of_birth ? profile.date_of_birth.slice(0,10) : "",
          citizenship:              profile.citizenship || "",
          gender:                   profile.gender || "",
          ethnicity:                profile.ethnicity || "",
          is_first_generation:      profile.is_first_generation || false,
          gpa_unweighted:           profile.gpa_unweighted ?? "",
          gpa_weighted:             profile.gpa_weighted ?? "",
          sat_score:                profile.sat_score ?? "",
          act_score:                profile.act_score ?? "",
          current_school:           profile.current_school || "",
          high_school_name:         profile.high_school_name || "",
          high_school_city:         profile.high_school_city || "",
          high_school_state:        profile.high_school_state || "",
          graduation_date:          profile.graduation_date ? profile.graduation_date.slice(0,7) : "",
          class_rank:               profile.class_rank_text || "",
          psat_score:               profile.psat_score ?? "",
          ap_courses:               profile.ap_courses || [],
          ib_courses:               profile.ib_courses || [],
          degree_type:              profile.degree_type || "",
          act_science_score:        profile.act_science_score ?? "",
          intended_majors:          profile.intended_majors || [],
          career_interests:         profile.career_interests || [],
          financial_aid_needed:     profile.financial_aid_needed || false,
          sports:                   profile.sports || [],
          clubs:                    profile.clubs || [],
          leadership_roles:         profile.leadership_roles || [],
          awards_honors:            profile.awards_honors || [],
          volunteer_hours:          profile.volunteer_hours ?? "",
          other_orgs:               profile.other_orgs || [],
          preferred_regions:        profile.preferred_regions || [],
          target_schools:           profile.target_schools || [],
          military_interest:        profile.military_interest || false,
          military_details:         profile.military_details || [],
          intended_graduate_program: profile.intended_graduate_program || "",
          household_income_bracket: profile.household_income_bracket || "",
          household_size:           profile.household_size ?? "",
          in_college_count:         profile.in_college_count ?? "",
          dependency_status:        profile.dependency_status || "",
          parent_employer_1:        profile.parent_employer_1 || "",
          parent_employer_2:        profile.parent_employer_2 || "",
        });
      })
      .catch(err => console.error("Failed to load profile:", err));
  }, [isEditMode, studentId]); 
    function handleChange(field, value) {
      setData(d => ({ ...d, [field]: value }));
      if (errors[field]) setErrors(e => { const n = { ...e }; delete n[field]; return n; });
    }

    // ── Step validation (client-side, mirrors server rules) ─────────────────
    function validateStep0() {
      const errs = {};
      if (!data.first_name.trim())       errs.first_name        = "First name is required.";
      if (!data.last_name.trim())        errs.last_name         = "Last name is required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
                                        errs.email             = "Enter a valid email address.";
      if (!data.grade_level)             errs.grade_level       = "Grade level is required.";
      if (!data.state_of_residence)      errs.state_of_residence = "State of residence is required.";
      return errs;
    }

    function validateStep1() {
      const errs = {};
      if (isCollegeOrAbove(data.grade_level) && (!data.current_school || data.current_school.trim() === ""))
        errs.current_school = "Current college / university is required.";
      const gpaU = parseFloat(data.gpa_unweighted);
      const gpaW = parseFloat(data.gpa_weighted);
      const sat  = parseInt(data.sat_score, 10);
      const act  = parseInt(data.act_score, 10);
      // At least one GPA is required
      if (data.gpa_unweighted === "" && data.gpa_weighted === "")
        errs.gpa_unweighted = "Please enter at least one GPA (unweighted or weighted).";
      if (data.gpa_unweighted !== "" && (isNaN(gpaU) || gpaU < 0 || gpaU > 5))
        errs.gpa_unweighted = "GPA (unweighted) must be between 0.0 and 5.0.";
      if (data.gpa_weighted !== "" && (isNaN(gpaW) || gpaW < 0 || gpaW > 6))
        errs.gpa_weighted = "GPA (weighted) must be between 0.0 and 6.0.";
      if (data.sat_score !== "" && (isNaN(sat) || sat < 400 || sat > 1600))
        errs.sat_score = "SAT score must be between 400 and 1600.";
      if (data.act_score !== "" && (isNaN(act) || act < 1 || act > 36))
      errs.act_score = "ACT score must be between 1 and 36.";
    const actSci = parseInt(data.act_science_score, 10);
    if (data.act_science_score !== "" && (isNaN(actSci) || actSci < 1 || actSci > 36))
      errs.act_science_score = "ACT Science score must be between 1 and 36.";
    return errs;
    }

    function validateStep2() {
      const errs = {};
      if (data.intended_majors.length === 0)
        errs.intended_majors = "Please enter at least one intended major.";
      return errs;
    }
    function computeWarns() {
      return {
        gpa_unweighted:   data.gpa_unweighted === "",
        intended_majors:  data.intended_majors.length === 0,
        financial_aid_needed: false, // checkbox default false is meaningful, not missing
      };
    }

    function handleNext() {
      let errs = {};
      if (step === 0) errs = validateStep0();
      if (step === 1) errs = validateStep1();
      if (step === 2) errs = validateStep2();
      if (Object.keys(errs).length > 0) { setErrors(errs); return; }
      setErrors({});
      setApiError("");           // Clear submission error on step change
      setStep(s => s + 1);
    }

    function handleBack() {
      setErrors({});
      setApiError("");           // Clear submission error on step change
      setStep(s => s - 1);
    }

    // ── Build payload ────────────────────────────────────────────────────────
    function buildPayload() {
      const p = { ...data };
      if (isParentMode) p.student_id = parentStudentId;

      // Coerce numerics — only include if non-empty
      ["gpa_unweighted","gpa_weighted"].forEach(f => {
        if (p[f] === "") delete p[f]; else p[f] = parseFloat(p[f]);
      });
      ["sat_score","act_score","act_science_score","psat_score","volunteer_hours",
       "household_size","in_college_count"].forEach(f => {
        if (p[f] === "") delete p[f]; else p[f] = parseInt(p[f], 10);
      });

      // Remove empty strings
      Object.keys(p).forEach(k => {
        if (p[k] === "") delete p[k];
      });
      // Normalize graduation_date from YYYY-MM to YYYY-MM-01 for DATE column
      if (p.graduation_date && /^\d{4}-\d{2}$/.test(p.graduation_date)) {
        p.graduation_date = p.graduation_date + "-01";
      }
      return p;
    }

    // ── Submit ───────────────────────────────────────────────────────────────
    async function handleSubmit() {
      // Final validation pass
      const errs0 = validateStep0();
      const errs1 = validateStep1();
      const errs2 = validateStep2();
      const allErrs = { ...errs0, ...errs1, ...errs2 };
      if (Object.keys(allErrs).length > 0) {
        setErrors(allErrs);
        setApiError("Please fix the errors above before submitting.");
        return;
      }

      // Compute strongly-recommended warnings (non-blocking)
      setWarns(computeWarns());

      setStatus("submitting");
      setApiError("");

      try {
        const res = await fetch(`${API_URL}/api/students`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify(buildPayload()),
        });

        const json = await res.json();

        if (res.status === 400) {
          // Map server field errors back to our errors state
          const serverErrs = {};
          (json.errors || []).forEach(e => { serverErrs[e.field] = e.message; });
          setErrors(serverErrs);
          setApiError("Some fields need attention. Please review and resubmit.");
          setStatus("idle");
          return;
        }

        if (!res.ok || !json.success) {
          setApiError(json.error || "Something went wrong. Please try again.");
          setStatus("error");
          return;
        }

        const studentId = json.student_id;
        studentIdRef.current = studentId;
        if (setCurrentStudentId) setCurrentStudentId(studentId);
        setStatus("polling");

        pollForMatches(
          json.student_id,
          () => navigate(isParentMode ? '/parent/dashboard' : `/dashboard/${json.student_id}`),
          () => setStatus("timeout"),
        );

      } catch (err) {
        console.error("Submit error:", err);
        setApiError("Could not reach the server. Check your connection and try again.");
        setStatus("error");
      }
    }

    function handleRetryPoll() {
      const studentId = studentIdRef.current;
      if (!studentId) { setStatus("idle"); return; }
      setStatus("polling");
      pollForMatches(studentId, () => navigate(`/dashboard/${studentId}`), () => setStatus("timeout"));
    }

    // ── Render ───────────────────────────────────────────────────────────────

    const isLastStep = step === STEPS.length - 1;
    if (status === "polling") {  
      return (
        <div style={styles.page}>
          <div style={styles.card}>
            <div style={styles.pollingWrap}>
              <div style={styles.spinner} />
              <h2 style={styles.pollingTitle}>Finding your scholarships…</h2>
              <p style={styles.pollingText}>
                Our AI is analyzing your profile. This takes up to 60 seconds.
                You'll be taken to your results automatically.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (status === "timeout") {
      return (
        <div style={styles.page}>
          <div style={styles.card}>
            <div style={styles.pollingWrap}>
              <p style={{ fontSize: "3rem" }}>⏱</p>
              <h2 style={styles.pollingTitle}>Still working…</h2>
              <p style={styles.pollingText}>
                Matching is taking longer than expected. Your profile was saved successfully.
              </p>
              <button style={styles.btnPrimary} onClick={handleRetryPoll}>
                Check Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={styles.page}>
        <AppHeader />

        <div style={styles.card}>
          <h1 style={styles.title}>{isEditMode ? "Update Your Profile" : isParentMode ? "Build Your Child's Profile" : "Build Your Profile"}</h1>
          <p style={styles.subtitle}>
            {isParentMode
              ? "Fill out your child's information so we can find scholarships tailored to them."
              : "Tell us about yourself so we can find scholarships tailored to you."}
          </p>

          <StepIndicator current={step} />

          <div style={styles.formBody}>
            {step === 0 && <StepIdentity  data={data} onChange={handleChange} errors={errors} />}
            {step === 1 && <StepAcademic  data={data} onChange={handleChange} errors={errors} />}
            {step === 2 && <StepProfile   data={data} onChange={handleChange} warns={warns} errors={errors} />}
            {step === 3 && <StepFinancial data={data} onChange={handleChange} />}
          </div>

          {apiError && <p style={styles.apiError}>{apiError}</p>}

          <div style={styles.navRow}>
            <button
              style={step === 0 ? styles.btnGhost : styles.btnSecondary}
              onClick={step === 0 ? () => navigate("/") : handleBack}
            >
              {step === 0 ? "← Back to Home" : "← Back"}
            </button>

            {isLastStep ? (
              <button
                style={{ ...styles.btnPrimary, opacity: status === "submitting" ? 0.7 : 1 }}
                onClick={handleSubmit}
                disabled={status === "submitting"}
              >
                {status === "submitting" ? "Submitting…" : isEditMode ? "Update My Profile →" : isParentMode ? "Find Scholarships →" : "Find My Scholarships →"}
              </button>
            ) : (
              <button style={styles.btnPrimary} onClick={handleNext}>
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Styles ─────────────────────────────────────────────────────────────────

  const styles = {
    page: {
      minHeight: "100vh",
      background: "linear-gradient(160deg, #f0f4f8 0%, #e8edf2 100%)",
      fontFamily: "'Georgia', 'Times New Roman', serif",
      paddingBottom: "60px",
    },
    header: {
      padding: "20px 40px",
      borderBottom: "1px solid #dce3eb",
      background: "#fff",
    },
    logo: {
      color: "#1a3a5c",
      fontWeight: 700,
      fontSize: "1.2rem",
      textDecoration: "none",
      letterSpacing: "0.02em",
    },
    card: {
      maxWidth: "780px",
      margin: "40px auto 40px",
      background: "#fff",
      borderRadius: "12px",
      boxShadow: "0 4px 24px rgba(26,58,92,0.10)",
      padding: "32px 24px",
    },
    title: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#1a3a5c",
      margin: "0 0 8px",
      letterSpacing: "-0.02em",
    },
    subtitle: {
      color: "#6b7a8d",
      fontSize: "1rem",
      margin: "0 0 36px",
    },

    // Step indicator
    stepRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: "40px",
      gap: 0,
    },
    stepItem: {
      display: "flex",
      alignItems: "center",
      flex: 1,
      position: "relative",
    },
    stepCircle: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      border: "2px solid",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "0.8rem",
      fontWeight: 700,
      flexShrink: 0,
      fontFamily: "sans-serif",
      transition: "all 0.2s",
    },
    stepLabel: {
      fontSize: "0.75rem",
      marginLeft: "8px",
      whiteSpace: "nowrap",
      fontFamily: "sans-serif",
      transition: "all 0.2s",
    },
    stepLine: {
      flex: 1,
      height: "2px",
      margin: "0 12px",
      transition: "background 0.2s",
    },

    // Section
    sectionHeading: {
      fontSize: "1.25rem",
      fontWeight: 700,
      color: "#1a3a5c",
      margin: "0 0 6px",
      borderBottom: "2px solid #c9a84c",
      paddingBottom: "8px",
    },
    sectionSubtitle: {
      color: "#6b7a8d",
      fontSize: "0.9rem",
      margin: "0 0 28px",
      fontFamily: "sans-serif",
    },
    formBody: {
      marginBottom: "32px",
    },

    // Form elements
    formGroup: {
      marginBottom: "20px",
      flex: 1,
      minWidth: "240px",
    },
    row: {
      display: "flex",
      gap: "20px",
      flexWrap: "wrap",
    },
    label: {
      display: "block",
      fontSize: "0.85rem",
      fontWeight: 600,
      color: "#2c3e50",
      marginBottom: "6px",
      fontFamily: "sans-serif",
      letterSpacing: "0.01em",
    },
    required: {
      color: "#c0392b",
    },
    input: {
      width: "100%",
      boxSizing: "border-box",
      padding: "10px 14px",
      border: "1.5px solid #d0d8e4",
      borderRadius: "6px",
      fontSize: "0.95rem",
      color: "#2c3e50",
      fontFamily: "sans-serif",
      background: "#fafcff",
      outline: "none",
      transition: "border-color 0.15s",
    },
    hint: {
      fontSize: "0.78rem",
      color: "#8a9ab0",
      margin: "4px 0 0",
      fontFamily: "sans-serif",
    },
    checkRow: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "0.92rem",
      color: "#2c3e50",
      fontFamily: "sans-serif",
      cursor: "pointer",
    },
    checkbox: {
      width: "16px",
      height: "16px",
      cursor: "pointer",
    },

    // Tag input
    tagRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "6px",
      marginTop: "8px",
    },
    tag: {
      background: "#e8f0fb",
      color: "#1a3a5c",
      borderRadius: "20px",
      padding: "3px 12px",
      fontSize: "0.78rem",
      fontFamily: "sans-serif",
      fontWeight: 500,
    },

    // Errors & warnings
    fieldError: {
      color: "#c0392b",
      fontSize: "0.8rem",
      margin: "4px 0 0",
      fontFamily: "sans-serif",
    },
    fieldWarn: {
      color: "#856404",
      background: "#fff8e1",
      border: "1px solid #ffe082",
      borderRadius: "5px",
      padding: "6px 10px",
      fontSize: "0.8rem",
      margin: "6px 0 0",
      fontFamily: "sans-serif",
    },
    apiError: {
      color: "#c0392b",
      background: "#fdf0ef",
      border: "1px solid #f5c6cb",
      borderRadius: "6px",
      padding: "10px 14px",
      fontSize: "0.88rem",
      fontFamily: "sans-serif",
      marginBottom: "20px",
    },

    // Strongly recommended block
    recommendedBlock: {
      background: "#fffbf0",
      border: "1.5px solid #ffe082",
      borderRadius: "8px",
      padding: "20px 24px",
      marginBottom: "28px",
    },
    recommendedLabel: {
      fontSize: "0.78rem",
      fontWeight: 700,
      color: "#856404",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      fontFamily: "sans-serif",
      margin: "0 0 16px",
    },

    // Review block on final step
    reviewBlock: {
      marginTop: "32px",
      background: "#f0f6ff",
      border: "1.5px solid #b8d0f0",
      borderRadius: "8px",
      padding: "24px",
    },
    reviewTitle: {
      fontSize: "1rem",
      fontWeight: 700,
      color: "#1a3a5c",
      margin: "0 0 8px",
      fontFamily: "sans-serif",
    },
    reviewText: {
      color: "#4a6280",
      fontSize: "0.9rem",
      margin: 0,
      fontFamily: "sans-serif",
    },

    // Navigation
    navRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      paddingTop: "24px",
      borderTop: "1px solid #e8edf2",
    },
    btnPrimary: {
      background: "#1a3a5c",
      color: "#fff",
      border: "none",
      borderRadius: "7px",
      padding: "12px 28px",
      fontSize: "0.95rem",
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "sans-serif",
      letterSpacing: "0.01em",
      transition: "background 0.15s",
    },
    btnSecondary: {
      background: "transparent",
      color: "#1a3a5c",
      border: "1.5px solid #1a3a5c",
      borderRadius: "7px",
      padding: "11px 24px",
      fontSize: "0.92rem",
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "sans-serif",
    },
    btnGhost: {
      background: "transparent",
      color: "#6b7a8d",
      border: "none",
      padding: "11px 0",
      fontSize: "0.92rem",
      cursor: "pointer",
      fontFamily: "sans-serif",
    },

    // Polling / timeout screens
    pollingWrap: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "320px",
      gap: "20px",
      textAlign: "center",
    },
    spinner: {
      width: "52px",
      height: "52px",
      border: "5px solid #e8edf2",
      borderTop: "5px solid #1a3a5c",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    pollingTitle: {
      fontSize: "1.5rem",
      fontWeight: 700,
      color: "#1a3a5c",
      margin: 0,
    },
    pollingText: {
      color: "#6b7a8d",
      fontSize: "0.95rem",
      maxWidth: "420px",
      margin: 0,
      fontFamily: "sans-serif",
    },
  };

  // Inject keyframe for spinner
  const styleEl = document.createElement("style");
  styleEl.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);